import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Button, Table, Form, Badge, ProgressBar, Modal } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { showToast } from '../../hooks/useToast';
import { User } from '../../models/User';

const Users = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({ teachers: { total: 0, synced: 0 }, students: { total: 0, synced: 0 }, coordinators: { total: 0, synced: 0 } });
  const [unsyncedUsers, setUnsyncedUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [roleFilter, setRoleFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isProcessing, setIsProcessing] = useState(false);
  const [migrationProgress, setMigrationProgress] = useState({ show: false, current: 0, total: 0, role: '' });

  useEffect(() => {
    loadStats();
    loadUnsyncedUsers();
  }, [currentPage, roleFilter]);

  const loadStats = async () => {
    try {
      const response = await api.get('/users/stats');
      setStats(response.data);
    } catch (error) {
      showToast('Error cargando estadísticas', 'danger');
    }
  };

  const loadUnsyncedUsers = async () => {
    try {
      const response = await api.get(`/users/unsynced?page=${currentPage}&limit=20&role=${roleFilter}`);
      setUnsyncedUsers(response.data.users.map(user => new User(user)));
      setTotalPages(response.data.totalPages);
    } catch (error) {
      showToast('Error cargando usuarios', 'danger');
    }
  };

  const migrateUsers = async (role) => {
    if (isProcessing) return;
    setIsProcessing(true);

    const roleText = role === 'teachers' ? 'profesores' : role === 'students' ? 'estudiantes' : 'coordinadores';
    const totalUsers = role === 'teachers' ? stats.teachers.total - stats.teachers.synced :
      role === 'students' ? stats.students.total - stats.students.synced :
        stats.coordinators.total - stats.coordinators.synced;

    setMigrationProgress({ show: true, current: 0, total: totalUsers, role: roleText });

    try {
      let hasMore = true;
      let totalProcessed = 0;

      while (hasMore) {
        const response = await api.post(`/users/migrate/${role}`, { batchSize: 10 });

        totalProcessed += response.data.processed;
        hasMore = response.data.hasMore;

        setMigrationProgress(prev => ({ ...prev, current: totalProcessed }));

        if (response.data.processed === 0) break;
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      showToast(`${roleText} migrados: ${totalProcessed}`, 'success');
      await loadStats();
      await loadUnsyncedUsers();
    } catch (error) {
      showToast(`Error migrando ${roleText}`, 'danger');
    } finally {
      setIsProcessing(false);
      setMigrationProgress({ show: false, current: 0, total: 0, role: '' });
    }
  };

  const syncSelectedUsers = async () => {
    if (selectedUsers.length === 0) return;

    try {
      const response = await api.post('/users/sync-batch', { userIds: selectedUsers });
      showToast(`${response.data.synced} usuarios sincronizados`, 'success');
      setSelectedUsers([]);
      await loadStats();
      await loadUnsyncedUsers();
    } catch (error) {
      showToast('Error sincronizando usuarios', 'danger');
    }
  };

  const syncSingleUser = async (userId) => {
    try {
      await api.post(`/users/sync/${userId}`);
      showToast('Usuario sincronizado', 'success');
      await loadStats();
      await loadUnsyncedUsers();
    } catch (error) {
      showToast('Error sincronizando usuario', 'danger');
    }
  };

  const handleUserSelection = (userId, checked) => {
    if (checked) {
      setSelectedUsers([...selectedUsers, userId]);
    } else {
      setSelectedUsers(selectedUsers.filter(id => id !== userId));
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedUsers(unsyncedUsers.map(user => user.id));
    } else {
      setSelectedUsers([]);
    }
  };

  const getProgressPercentage = (synced, total) => total > 0 ? Math.round((synced / total) * 100) : 0;

  const StatCard = ({ title, stats, color, onMigrate, role }) => {
    const percentage = getProgressPercentage(stats.synced, stats.total);
    const pending = stats.total - stats.synced;

    return (
      <Card className="h-100 border-0 shadow-sm">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="mb-0">{title}</h6>
            <i className={`bi ${role === 'teachers' ? 'bi-person-badge' : role === 'coordinators' ? 'bi-person-gear' : 'bi-mortarboard'} text-${color} fs-4`}></i>
          </div>

          <div className="mb-3">
            <div className="d-flex justify-content-between mb-1">
              <small className="text-muted">Progreso</small>
              <small className={`fw-bold text-${color}`}>{percentage}%</small>
            </div>
            <ProgressBar now={percentage} variant={color} className="mb-2" style={{ height: '8px' }} />
          </div>

          <Row className="text-center mb-3">
            <Col>
              <div className={`h5 text-${color} mb-0`}>{stats.synced}</div>
              <small className="text-muted">Migrados</small>
            </Col>
            <Col>
              <div className="h5 text-warning mb-0">{pending}</div>
              <small className="text-muted">Pendientes</small>
            </Col>
            <Col>
              <div className="h5 text-secondary mb-0">{stats.total}</div>
              <small className="text-muted">Total</small>
            </Col>
          </Row>

          <Button
            variant={pending > 0 ? color : 'outline-secondary'}
            size="sm"
            className="w-100"
            onClick={() => onMigrate(role)}
            disabled={isProcessing || pending === 0}
          >
            <i className="bi bi-cloud-upload me-1"></i>
            {pending === 0 ? 'Completado' : isProcessing ? 'Migrando...' : `Migrar ${pending}`}
          </Button>
        </Card.Body>
      </Card>
    );
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Migración de Usuarios</h2>
        <div className="d-flex gap-2">
          <Button
            variant="outline-primary"
            onClick={() => navigate('/users/compare')}
          >
            <i className="bi bi-arrow-left-right me-2"></i>
            Comparar y Vincular
          </Button>
          <div className="vr my-1"></div>
          <Badge bg="success" className="d-flex align-items-center">{stats.teachers.synced + stats.students.synced + stats.coordinators.synced} Migrados</Badge>
          <Badge bg="warning" className="d-flex align-items-center">{(stats.teachers.total - stats.teachers.synced) + (stats.students.total - stats.students.synced) + (stats.coordinators.total - stats.coordinators.synced)} Pendientes</Badge>
        </div>
      </div>

      <Row className="g-4 mb-4">
        <Col md={4}>
          <StatCard
            title="Coordinadores"
            stats={stats.coordinators}
            color="primary"
            onMigrate={migrateUsers}
            role="coordinators"
          />
        </Col>
        <Col md={4}>
          <StatCard
            title="Profesores"
            stats={stats.teachers}
            color="info"
            onMigrate={migrateUsers}
            role="teachers"
          />
        </Col>
        <Col md={4}>
          <StatCard
            title="Estudiantes"
            stats={stats.students}
            color="success"
            onMigrate={migrateUsers}
            role="students"
          />
        </Col>
      </Row>

      <Card>
        <Card.Header className="bg-light">
          <div className="d-flex justify-content-between align-items-center">
            <h6 className="mb-0">
              <i className="bi bi-exclamation-triangle text-warning me-2"></i>
              Usuarios Pendientes de Migración
            </h6>
            <div className="btn-group btn-group-sm">
              {['all', 'coordinators', 'teachers', 'students'].map(filter => (
                <Button
                  key={filter}
                  variant={roleFilter === filter ? 'primary' : 'outline-primary'}
                  size="sm"
                  onClick={() => setRoleFilter(filter)}
                >
                  {filter === 'all' ? 'Todos' : filter === 'coordinators' ? 'Coordinadores' : filter === 'teachers' ? 'Profesores' : 'Estudiantes'}
                </Button>
              ))}
            </div>
          </div>
        </Card.Header>

        <Card.Body className="p-0">
          {unsyncedUsers.length === 0 ? (
            <div className="text-center py-5">
              <i className="bi bi-check-circle text-success" style={{ fontSize: '3rem' }}></i>
              <h5 className="mt-3 text-success">¡Todos los usuarios están migrados!</h5>
              <p className="text-muted">No hay usuarios pendientes de migración</p>
            </div>
          ) : (
            <>
              <div className="p-3 border-bottom bg-light">
                <div className="d-flex justify-content-between align-items-center">
                  <Form.Check
                    type="checkbox"
                    label={`Seleccionar todos (${unsyncedUsers.length})`}
                    checked={selectedUsers.length === unsyncedUsers.length}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                  />
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={syncSelectedUsers}
                    disabled={selectedUsers.length === 0}
                  >
                    <i className="bi bi-cloud-upload me-1"></i>
                    Migrar Seleccionados ({selectedUsers.length})
                  </Button>
                </div>
              </div>

              <Table hover className="mb-0">
                <thead className="table-light">
                  <tr>
                    <th width="50"></th>
                    <th>Usuario</th>
                    <th>Email</th>
                    <th>Rol</th>
                    <th width="100">Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {unsyncedUsers.map(user => (
                    <tr key={user.id}>
                      <td>
                        <Form.Check
                          type="checkbox"
                          checked={selectedUsers.includes(user.id)}
                          onChange={(e) => handleUserSelection(user.id, e.target.checked)}
                        />
                      </td>
                      <td>
                        <div className="d-flex align-items-center">
                          <div className={`bg-${user.role === 'Coordinador' ? 'primary' : user.isTeacher ? 'info' : 'success'} text-white rounded-circle d-flex align-items-center justify-content-center me-3`} style={{ width: '40px', height: '40px' }}>
                            <i className={`bi ${user.role === 'Coordinador' ? 'bi-person-gear' : user.isTeacher ? 'bi-person-badge' : 'bi-mortarboard'}`}></i>
                          </div>
                          <div>
                            <div className="fw-medium">{user.fullName}</div>
                            <small className="text-muted">ID: {user.idNumber || user.id}</small>
                          </div>
                        </div>
                      </td>
                      <td>
                        <small className="text-muted">{user.email || 'Sin email'}</small>
                      </td>
                      <td>
                        <Badge bg={user.role === 'Coordinador' ? 'primary' : user.isTeacher ? 'info' : 'success'}>
                          {user.role}
                        </Badge>
                      </td>
                      <td>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => syncSingleUser(user.id)}
                        >
                          <i className="bi bi-cloud-upload"></i>
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              {totalPages > 1 && (
                <div className="p-3 border-top">
                  <div className="d-flex justify-content-center">
                    <div className="btn-group">
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage(currentPage - 1)}
                      >
                        <i className="bi bi-chevron-left"></i>
                      </Button>
                      <Button variant="outline-secondary" size="sm" disabled>
                        {currentPage} de {totalPages}
                      </Button>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        disabled={currentPage === totalPages}
                        onClick={() => setCurrentPage(currentPage + 1)}
                      >
                        <i className="bi bi-chevron-right"></i>
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>

      <Modal show={migrationProgress.show} backdrop="static" keyboard={false} centered>
        <Modal.Body className="text-center py-4">
          <div className="mb-3">
            <div className="spinner-border text-primary mb-3" role="status">
              <span className="visually-hidden">Migrando...</span>
            </div>
            <h5>Migrando {migrationProgress.role}</h5>
            <p className="text-muted mb-0">
              {migrationProgress.current} de {migrationProgress.total} usuarios procesados
            </p>
          </div>

          <ProgressBar
            now={migrationProgress.total > 0 ? (migrationProgress.current / migrationProgress.total) * 100 : 0}
            variant="primary"
            className="mb-3"
            style={{ height: '10px' }}
          />

          <div className="text-muted">
            {migrationProgress.total > 0 ? Math.round((migrationProgress.current / migrationProgress.total) * 100) : 0}% completado
          </div>
        </Modal.Body>
      </Modal>
    </div >
  );
};

export default Users;